import React, { useState, useEffect, useCallback } from "react";
import { Container, Form, Button, Card, Spinner } from "react-bootstrap";
import api from "../api/api";
import { useParams, useNavigate } from "react-router-dom";
import { logger } from "../utils/logger";
import { useAlert } from "../context/AlertContext";

const EditClient = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [receivesSMS, setReceivesSMS] = useState(false);
  const [receivesEmail, setReceivesEmail] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { clientId } = useParams();
  const { showAlert } = useAlert();

  const navigate = useNavigate();

  const fetchClientDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/users/client_details/?id=${clientId}`);
      const clientData = response.data;

      setFirstName(clientData.first_name);
      setLastName(clientData.last_name);
      setEmail(clientData.email);
      setMobile(clientData.mobile);
      setReceivesSMS(clientData.receives_sms);
      setReceivesEmail(clientData.receives_email);
      setDateOfBirth(clientData.date_of_birth);

      setLoading(false);
    } catch (error) {
      showAlert("danger", "Failed to load client details");
      setLoading(false);
      console.error(error);
    }
  },[clientId, showAlert]);
  useEffect(() => {
    fetchClientDetails();
  }, [clientId, fetchClientDetails]);

  const handleUpdateClient = async (event) => {
    event.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await api.put(`/users/edit_client/`, {
        id: clientId,
        first_name: firstName,
        last_name: lastName,
        date_of_birth: dateOfBirth,
        email,
        mobile,
        receives_sms: receivesSMS,
        receives_email: receivesEmail,
      });

      // Only navigate if the response indicates success
      if (response.status === 200) {
        showAlert("success", "Client updated successfully!");
        navigate("/clients");
      } else {
        showAlert("danger", "Failed to update client. Please try again.");
      }
    } catch (error) {
      logger.error("error:", { url: "/users/edit_client/", error });
      const serverError = error.response?.data;
      if (serverError) {
        if (typeof serverError === "string") {
          setError(serverError);
        } else if (serverError.error) {
          setError(serverError.error);
        } else {
          setError(JSON.stringify(serverError));
        }
      } else {
        showAlert("danger", "Failed to update client. Please try again.");
      }
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "50vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="d-flex justify-content-center align-items-center py-4 mb-5">
      <div className="row w-100 justify-content-center">
        <div className="col-12 col-md-6">
          <Card.Body>
            <h2 className="text-center mb-4">Edit Client</h2>

            <Form onSubmit={handleUpdateClient}>
              <Form.Group className="mb-3" controlId="formFirstName">
                <Form.Control
                  type="text"
                  name="first_name"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="formLastName">
                <Form.Control
                  type="text"
                  name="last_name"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="formDateOfBirth">
                <h6>Date of Birth</h6>
                <Form.Control
                  type="date"
                  name="date_of_birth"
                  placeholder="Date of Birth"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="formEmail">
                <Form.Control
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="formMobile">
                <Form.Control
                  type="tel"
                  name="mobile"
                  placeholder="Mobile"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  required
                />
              </Form.Group>

              <Button variant="primary" type="submit" className="w-100" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updating...
                  </>
                ) : (
                  "Update Client"
                )}
              </Button>

              {error && (
                <div className="text-center mt-3 text-danger">{error}</div>
              )}
            </Form>
          </Card.Body>
        </div>
      </div>
    </Container>
  );
};

export default EditClient;
